import { getFirestore, doc, updateDoc, Timestamp, getDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { ITEM_TYPE_ROOM_EQ_SUBMISSION } from './profileTabMigration';

/**
 * Database utility functions for managing the file and folder system
 * This handles all Firestore operations for files and folders
 */

/**
 * Get or create the protected "User Uploads" folder for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<string>} - The folder ID of the User Uploads folder
 */
export const getOrCreateUserUploadsFolder = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        
        const userData = userDoc.data();
        const existingFolders = userData.folders || {};
        
        // Check if User Uploads folder already exists
        const existingProtectedFolder = Object.entries(existingFolders).find(([id, folder]) => 
            folder.protected === true && folder.name === 'User Uploads'
        );
        
        if (existingProtectedFolder) {
            return existingProtectedFolder[0]; // Return existing folder ID
        }
        
        // Create new User Uploads folder
        const userUploadsFolderId = `folder_${Date.now()}_protected_${userId}`;
        const protectedFolderData = {
            name: 'User Uploads',
            color: '#2ecc71',
            parentId: null,
            order: 0, // Put it first
            protected: true, // Prevent deletion
            backgroundImg: '',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };
        
        const updatedFolders = { ...existingFolders, [userUploadsFolderId]: protectedFolderData };
        
        await updateDoc(userRef, { folders: updatedFolders });
        
        console.log(`✅ Created User Uploads folder for user ${userId}`);
        return userUploadsFolderId;
        
    } catch (error) {
        console.error('Error getting/creating User Uploads folder:', error);
        throw error;
    }
};

/**
 * Ensure a venue is in the user's User Uploads (root) folder. If the venue is not in any folder, add it.
 * Used when user adds a Room EQ so they can revisit the venue in Media Manager.
 * @param {string} userId - The user's ID
 * @param {object} venueData - Venue data { id, name, city, state, capacity, ... }
 * @param {object} savedVenueImage - Optional saved venue image URL (from userData.savedVenueImages[venueId])
 * @returns {Promise<boolean>} - true if venue was added, false if already present
 */
export const ensureVenueInUserUploads = async (userId, venueData, savedVenueImage = null) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) return false;
        const userData = userDoc.data();
        const currentFiles = userData.files || {};
        const venueId = venueData.id;
        const alreadyInFolder = Object.values(currentFiles).some(
            (f) => f.itemType === 'savedVenue' && f.data?.id === venueId
        );
        if (alreadyInFolder) return false;
        const userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
        const userDoc2 = await getDoc(userRef);
        const latestFiles = userDoc2.data()?.files || {};
        const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const venueFileData = {
            name: venueData.name || '',
            id: fileId,
            parentId: userUploadsFolderId,
            itemType: 'savedVenue',
            data: {
                id: venueId,
                name: venueData.name || '',
                city: venueData.city || '',
                state: venueData.state || '',
                capacity: venueData.capacity || '',
                imageUrl: savedVenueImage || '',
                url: savedVenueImage || ''
            },
            order: Object.values(latestFiles).filter((f) => f.parentId === userUploadsFolderId).length,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };
        const updatedFiles = { ...latestFiles, [fileId]: venueFileData };
        await updateDoc(userRef, { files: updatedFiles });
        return true;
    } catch (error) {
        console.error('ensureVenueInUserUploads error:', error);
        return false;
    }
};

/** True if the file sits anywhere under a Tour Planner folder (those rows belong to My Tours, not Saved Venues). */
const fileIsUnderTourFolder = (file, folders) => {
    let cur = file?.parentId;
    const seen = new Set();
    while (cur && !seen.has(cur)) {
        seen.add(cur);
        const folder = folders[cur];
        if (!folder) return false;
        if (folder.tour || folder.sourceTourFolder) return true;
        cur = folder.parentId;
    }
    return false;
};

/** Saved Venues library rows for a venue (excludes tour copies). */
const findSavedVenueFileIds = (files, folders, venueId) =>
    Object.entries(files || {})
        .filter(([, f]) =>
            f?.itemType === 'savedVenue' &&
            f.data?.id === venueId &&
            !f.tourTabMirrorOf &&
            !fileIsUnderTourFolder(f, folders || {})
        )
        .map(([id]) => id);

/** Whether the user's library has this venue in Saved Venues. */
export const isVenueSaved = (userData, venueId) =>
    findSavedVenueFileIds(userData?.files, userData?.folders, venueId).length > 0;

/**
 * Save a venue into the single Saved Venues folder: the "My Saved Venues" tab root after
 * profile tab migration, otherwise User Uploads. No-op if the venue is already saved.
 * @returns {Promise<{ files, folders, savedVenues }>} - the user's library as written, to sync into the user context
 */
export const saveVenueToSavedVenues = async (userId, venueData) => {
    const db = getFirestore();
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) throw new Error('User document not found');
    const userData = userDoc.data();
    const venueId = venueData.id;
    if (isVenueSaved(userData, venueId)) {
        return { files: userData.files || {}, folders: userData.folders || {}, savedVenues: userData.savedVenues || [] };
    }

    const tabRootId = userData.profileTabRoots?.savedVenuesFolderId;
    const folderId = tabRootId && userData.folders?.[tabRootId]
        ? tabRootId
        : await getOrCreateUserUploadsFolder(userId);
    const latest = tabRootId === folderId ? userData : (await getDoc(userRef)).data();
    const latestFiles = latest.files || {};
    const imageUrl = latest.savedVenueImages?.[venueId] || '';
    const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const venueFileData = {
        name: venueData.name || '',
        id: fileId,
        parentId: folderId,
        itemType: 'savedVenue',
        data: {
            id: venueId,
            name: venueData.name || '',
            city: venueData.city || '',
            state: venueData.state || '',
            capacity: venueData.capacity || '',
            imageUrl,
            url: imageUrl
        },
        order: Object.values(latestFiles).filter((f) => f.parentId === folderId).length,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
    };
    const files = { ...latestFiles, [fileId]: venueFileData };
    await updateDoc(userRef, { files, savedVenues: arrayUnion(venueId) });
    const savedVenues = latest.savedVenues || [];
    return {
        files,
        folders: latest.folders || {},
        savedVenues: savedVenues.includes(venueId) ? savedVenues : [...savedVenues, venueId]
    };
};

/**
 * Remove a venue from Saved Venues (tour copies are left alone).
 * @returns {Promise<{ files, savedVenues }>} - the user's library as written, to sync into the user context
 */
export const removeVenueFromSavedVenues = async (userId, venueId) => {
    const db = getFirestore();
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) throw new Error('User document not found');
    const userData = userDoc.data();
    const files = { ...(userData.files || {}) };
    findSavedVenueFileIds(files, userData.folders, venueId).forEach((id) => {
        delete files[id];
    });
    await updateDoc(userRef, { files, savedVenues: arrayRemove(venueId) });
    return { files, savedVenues: (userData.savedVenues || []).filter((id) => id !== venueId) };
};

/**
 * Get or create the "My Room Notes" folder at root (same level as User Uploads, not inside it).
 * @param {string} userId - The user's ID
 * @returns {Promise<string>} - The folder ID of the My Room Notes folder
 */
export const getOrCreateRoomNotesFolder = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);

        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        const userData = userDoc.data();
        const existingFolders = userData.folders || {};

        // Look for an existing "My Room Notes" folder at root (parentId === null)
        const existingRoomNotesFolder = Object.entries(existingFolders).find(
            ([, folder]) => folder.name === 'My Room Notes' && folder.parentId === null
        );

        if (existingRoomNotesFolder) {
            return existingRoomNotesFolder[0];
        }

        // Create new My Room Notes folder at root (order: 1 so it appears after User Uploads which is 0)
        const roomNotesFolderId = `folder_${Date.now()}_room_notes_${userId}`;
        const roomNotesFolderData = {
            name: 'My Room Notes',
            color: '#9b59b6',
            parentId: null,
            order: 1,
            protected: true,
            backgroundImg: '',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        };

        const updatedFolders = { ...existingFolders, [roomNotesFolderId]: roomNotesFolderData };
        await updateDoc(userRef, { folders: updatedFolders });

        return roomNotesFolderId;
    } catch (error) {
        console.error('Error getting/creating My Room Notes folder:', error);
        throw error;
    }
};

/**
 * Folder that backs the profile "Saved Room Info" / Room EQ tab (`profileLibraryTab === 'roomEq'`).
 * After tab migration this is `profileTabRoots.roomNotesFolderId`; otherwise the legacy root "My Room Notes" folder.
 */
export const getRoomNotesTabRootFolderId = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) {
            return getOrCreateRoomNotesFolder(userId);
        }
        const userData = userDoc.data();
        const roots = userData.profileTabRoots || {};
        const fid = roots.roomNotesFolderId;
        const folders = userData.folders || {};
        if (fid && folders[fid]) {
            return fid;
        }
        return getOrCreateRoomNotesFolder(userId);
    } catch (error) {
        console.error('getRoomNotesTabRootFolderId error:', error);
        return getOrCreateRoomNotesFolder(userId);
    }
};

/**
 * Ensure a venue appears in the user's \"My Room Notes\" folder.
 * This is used when the user saves Room EQ notes so they can quickly
 * find that venue in Media Manager under My Room Notes.
 *
 * @param {string} userId
 * @param {object} venueData - { id, name, city, state, capacity, ... }
 * @param {string|null} savedVenueImage - optional image URL
 */
export const ensureVenueInRoomNotesFolder = async (userId, venueData, savedVenueImage = null) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) return false;

        const userData = userDoc.data();
        const currentFiles = userData.files || {};
        const venueId = venueData.id;

        // Tab root for Room EQ / room notes (matches profile "Saved Room Info" after migration)
        const roomNotesFolderId = await getRoomNotesTabRootFolderId(userId);

        // Check if this venue is already in My Room Notes
        const alreadyInRoomNotes = Object.values(currentFiles).some(
            (f) =>
                f.parentId === roomNotesFolderId &&
                f.itemType === 'savedVenue' &&
                f.data?.id === venueId
        );
        if (alreadyInRoomNotes) return false;

        const userDoc2 = await getDoc(userRef);
        const latestFiles = userDoc2.data()?.files || {};
        const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const venueFileData = {
            name: venueData.name || '',
            id: fileId,
            parentId: roomNotesFolderId,
            itemType: 'savedVenue',
            data: {
                id: venueId,
                name: venueData.name || '',
                city: venueData.city || '',
                state: venueData.state || '',
                capacity: venueData.capacity || '',
                imageUrl: savedVenueImage || '',
                url: savedVenueImage || '',
            },
            order: Object.values(latestFiles).filter((f) => f.parentId === roomNotesFolderId).length,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        };

        const updatedFiles = { ...latestFiles, [fileId]: venueFileData };
        await updateDoc(userRef, { files: updatedFiles });
        return true;
    } catch (error) {
        console.error('ensureVenueInRoomNotesFolder error:', error);
        return false;
    }
};

/**
 * Library shortcut for a Room EQ submission (same shape as profile tab migration).
 * Writes under {@link getRoomNotesTabRootFolderId} so it appears in Saved Room Info.
 */
export async function addRoomEqSubmissionLibraryFile(userId, { venueId, submissionId, venueName, previewImageUrl }) {
    try {
        if (!userId || !venueId || !submissionId) return null;
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) return null;

        const parentId = await getRoomNotesTabRootFolderId(userId);
        const fileId = `file_room_eq_sub_${venueId}_${submissionId}`;
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        if (existingFiles[fileId]) {
            return fileId;
        }

        const userDoc2 = await getDoc(userRef);
        const latestFiles = userDoc2.data()?.files || {};
        const nameBase = (venueName && String(venueName).trim()) || 'Venue';
        const fileData = {
            id: fileId,
            name: `${nameBase} room info`,
            parentId,
            itemType: ITEM_TYPE_ROOM_EQ_SUBMISSION,
            order: Object.values(latestFiles).filter((f) => f && f.parentId === parentId).length,
            data: {
                fileType: ITEM_TYPE_ROOM_EQ_SUBMISSION,
                venueId,
                submissionId,
                venueName: venueName || null,
                previewImageUrl: previewImageUrl || null,
            },
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        };
        const updatedFiles = { ...latestFiles, [fileId]: fileData };
        await updateDoc(userRef, { files: updatedFiles });
        return fileId;
    } catch (error) {
        console.error('addRoomEqSubmissionLibraryFile error:', error);
        return null;
    }
}

/**
 * Add a venue report card to the user's library (`itemType: 'report'`) under My Reports when migrated.
 * @param {string} userId
 * @param {object} reportDoc — must include `id` (venue_reports doc id), `venueId`, `venueName`, optional `photos`/`videos`/`documents` arrays
 * @returns {Promise<string|null>} created file id, or null on failure
 */
export async function addReportLibraryFileForUser(userId, reportDoc) {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) return null;
        const userData = userDoc.data() || {};
        const roots = userData.profileTabRoots || {};
        const parentId =
            roots.reportsFolderId ||
            roots.generalFolderId ||
            (await getOrCreateUserUploadsFolder(userId));
        const currentFiles = userData.files || {};
        const venueName = reportDoc.venueName || 'Report';
        const firstPhoto = (reportDoc.photos && reportDoc.photos[0]) || '';
        const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const reportFileData = {
            name: `${venueName} - Report`,
            id: fileId,
            parentId,
            itemType: 'report',
            data: {
                reportId: reportDoc.id,
                venueId: reportDoc.venueId,
                venueName,
                url: firstPhoto,
                imageUrl: firstPhoto,
                hasPhotos: Array.isArray(reportDoc.photos) && reportDoc.photos.length > 0,
                hasVideos: Array.isArray(reportDoc.videos) && reportDoc.videos.length > 0,
                hasDocuments: Array.isArray(reportDoc.documents) && reportDoc.documents.length > 0,
                status: reportDoc.status || 'pending',
                userType: reportDoc.userType,
                isTechSpecsOnly: !!reportDoc.isTechSpecsOnly,
                createdAt: reportDoc.createdAt || Timestamp.now(),
                dateOfPerformance: reportDoc.dateOfPerformance,
                bandName: reportDoc.bandName,
                datesWorked: reportDoc.datesWorked,
                jobTitle: reportDoc.jobTitle,
                notes: reportDoc.notes,
            },
            order: Object.values(currentFiles).filter((f) => f.parentId === parentId).length,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        };
        await updateDoc(userRef, { files: { ...currentFiles, [fileId]: reportFileData } });
        return fileId;
    } catch (e) {
        console.error('addReportLibraryFileForUser:', e);
        return null;
    }
}

/**
 * Get or create the "Discussion Board Media" subfolder within User Uploads
 * @param {string} userId - The user's ID
 * @returns {Promise<string>} - The folder ID of the Discussion Board Media subfolder
 */
export const getOrCreateDiscussionBoardMediaSubfolder = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        
        // First, get or create the User Uploads folder
        const userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
        
        console.log('User Uploads folder ID:', userUploadsFolderId);
        // Get fresh user data after potentially creating User Uploads folder
        const userDoc = await getDoc(userRef);
        console.log('User document:', userDoc.data());
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        console.log('User document exists');
        const userData = userDoc.data();
        const existingFolders = userData.folders || {};
        console.log('Existing folders:', existingFolders);
        // Check if Discussion Board Media subfolder already exists within User Uploads
        const existingDiscussionSubfolder = Object.entries(existingFolders).find(([id, folder]) => 
            folder.name === 'Discussion Board Media' && folder.parentId === userUploadsFolderId
        );
        
        if (existingDiscussionSubfolder) {
            console.log(`✅ Found existing Discussion Board Media subfolder for user ${userId}`);
            return existingDiscussionSubfolder[0]; // Return existing subfolder ID
        }
        console.log('No existing Discussion Board Media subfolder found');
        // Create new Discussion Board Media subfolder within User Uploads
        const discussionSubfolderId = `folder_${Date.now()}_discussion_sub_${userId}`;
        const discussionSubfolderData = {
            name: 'Discussion Board Media',
            color: '#3498db',
            parentId: userUploadsFolderId, // Parent is User Uploads folder
            order: 0, // First subfolder in User Uploads
            protected: false, // Allow deletion
            backgroundImg: '',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };
        console.log('Discussion subfolder data:', discussionSubfolderData);
        const updatedFolders = { ...existingFolders, [discussionSubfolderId]: discussionSubfolderData };
        console.log('Updated folders:', updatedFolders);
        await updateDoc(userRef, { folders: updatedFolders });
        
        console.log(`✅ Created Discussion Board Media subfolder within User Uploads for user ${userId}`);
        return discussionSubfolderId;
        
    } catch (error) {
        console.error('Error getting/creating Discussion Board Media subfolder:', error);
        throw error;
    }
};

/**
 * Add a file reference to the user's folder system
 * @param {string} userId - The user's ID
 * @param {string} fileName - Display name for the file
 * @param {string} fileUrl - Download URL of the file
 * @param {string} uploadSource - Source of upload ('venue_report', 'discussion', etc.)
 * @param {Object} metadata - Additional metadata for the file
 * @returns {Promise<string>} - The file ID that was created
 */
export const addFileToUserUploads = async (userId, fileName, fileUrl, uploadSource, metadata = {}) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Use custom target folder if provided, otherwise get or create User Uploads folder
        const userUploadsFolderId = metadata.targetFolderId || await getOrCreateUserUploadsFolder(userId);
        // Create file entry
        const fileId = `file_upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const fileData = {
            name: fileName,
            id: fileId,
            parentId: userUploadsFolderId,
            itemType: 'file',
            data: {
                fileType: metadata.mediaType || 'image', // Default to image if not specified
                url: fileUrl,
                manualUpload: false
            },
            order: Object.values(existingFiles).filter(f => f.parentId === userUploadsFolderId).length,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            uploadSource: uploadSource,
            storagePath: fileUrl, // Use URL as storage path for external uploads
            ...metadata // Spread any additional metadata
        };
        
        const updatedFiles = { ...existingFiles, [fileId]: fileData };
        
        await updateDoc(userRef, { files: updatedFiles });
        
        return fileId;
        
    } catch (error) {
        console.error('Error adding file to User Uploads:', error);
        // Don't throw error - we don't want to break the original upload process
        // Just log it and continue
        return null;
    }
};

/**
 * Add multiple files to User Uploads folder
 * @param {string} userId - The user's ID
 * @param {Array} files - Array of file objects with {name, url, metadata}
 * @param {string} uploadSource - Source of upload
 * @returns {Promise<Array>} - Array of created file IDs
 */
export const addMultipleFilesToUserUploads = async (userId, files, uploadSource) => {
    try {
        const fileIds = [];
        
        for (const file of files) {
            const fileId = await addFileToUserUploads(
                userId, 
                file.name, 
                file.url, 
                uploadSource, 
                file.metadata || {}
            );
            if (fileId) {
                fileIds.push(fileId);
            }
        }
        
        console.log(`✅ Added ${fileIds.length} files to User Uploads folder for user ${userId}`);
        return fileIds;
        
    } catch (error) {
        console.error('Error adding multiple files to User Uploads:', error);
        return [];
    }
};

/**
 * Add a file to the root folder (no specific folder)
 * @param {string} userId - The user's ID
 * @param {string} fileName - Display name for the file
 * @param {string} fileUrl - Download URL of the file
 * @param {string} uploadSource - Source of upload ('venue_report', 'discussion', etc.)
 * @param {Object} metadata - Additional metadata for the file
 * @returns {Promise<string>} - The file ID that was created
 */
export const addFileToRootFolder = async (userId, fileName, fileUrl, uploadSource, metadata = {}) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Create file entry for root folder (no folderId)
        const fileId = `file_root_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const fileData = {
            folderId: null, // Root folder - no specific folder
            name: fileName,
            url: fileUrl,
            storagePath: fileUrl, // Use URL as storage path for external uploads
            order: Object.values(existingFiles).filter(f => f.folderId === null).length,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            protected: false, // Root files are not protected by default
            uploadSource: uploadSource,
            ...metadata // Spread any additional metadata
        };
        
        const updatedFiles = { ...existingFiles, [fileId]: fileData };
        
        await updateDoc(userRef, { files: updatedFiles });
        
        console.log(`✅ Added file "${fileName}" to root folder for user ${userId}`);
        return fileId;
        
    } catch (error) {
        console.error('Error adding file to root folder:', error);
        // Don't throw error - we don't want to break the original upload process
        // Just log it and continue
        return null;
    }
};

/**
 * Remove a file from the User Uploads folder
 * @param {string} userId - The user's ID
 * @param {string} fileUrl - The URL of the file to remove
 * @returns {Promise<boolean>} - Whether the file was successfully removed
 */
export const removeFileFromUserUploads = async (userId, fileUrl) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            console.log('User document not found');
            return false;
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Find the file by URL
        const fileToRemove = Object.entries(existingFiles).find(([id, file]) => 
            file.url === fileUrl || file.storagePath === fileUrl
        );
        
        if (!fileToRemove) {
            console.log('File not found in User Uploads folder');
            return false;
        }
        
        const [fileId, fileData] = fileToRemove;
        
        // Remove the file from the files object
        const updatedFiles = { ...existingFiles };
        delete updatedFiles[fileId];
        
        // Update the user document
        await updateDoc(userRef, { files: updatedFiles });
        
        console.log(`✅ Removed file "${fileData.name}" from User Uploads folder for user ${userId}`);
        return true;
        
    } catch (error) {
        console.error('Error removing file from User Uploads:', error);
        return false;
    }
};

/**
 * Remove a file from the root folder
 * @param {string} userId - The user's ID
 * @param {string} fileUrl - The URL of the file to remove
 * @returns {Promise<boolean>} - Whether the file was successfully removed
 */
export const removeFileFromRootFolder = async (userId, fileUrl) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            console.log('User document not found');
            return false;
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Find the file by URL in root folder (folderId === null)
        const fileToRemove = Object.entries(existingFiles).find(([id, file]) => 
            (file.url === fileUrl || file.storagePath === fileUrl) && file.folderId === null
        );
        
        if (!fileToRemove) {
            console.log('File not found in root folder');
            return false;
        }
        
        const [fileId, fileData] = fileToRemove;
        
        // Remove the file from the files object
        const updatedFiles = { ...existingFiles };
        delete updatedFiles[fileId];
        
        // Update the user document
        await updateDoc(userRef, { files: updatedFiles });
        
        console.log(`✅ Removed file "${fileData.name}" from root folder for user ${userId}`);
        return true;
        
    } catch (error) {
        console.error('Error removing file from root folder:', error);
        return false;
    }
};

/**
 * Get files from the root folder
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} - Array of files in the root folder
 */
export const getRootFolderFiles = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            return [];
        }
        
        const userData = userDoc.data();
        const files = userData.files || {};
        
        // Get files from root folder (folderId === null)
        const rootFiles = Object.entries(files)
            .filter(([id, file]) => file.folderId === null)
            .map(([id, file]) => ({ id, ...file }))
            .sort((a, b) => a.order - b.order);
        
        return rootFiles;
        
    } catch (error) {
        console.error('Error getting root folder files:', error);
        return [];
    }
};

/**
 * Get user uploads statistics
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} - Statistics about user uploads
 */
export const getUserUploadsStats = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            return { hasUserUploadsFolder: false, totalFiles: 0, filesBySource: {} };
        }
        
        const userData = userDoc.data();
        const folders = userData.folders || {};
        const files = userData.files || {};
        
        // Find User Uploads folder
        const userUploadsFolder = Object.entries(folders).find(([id, folder]) => 
            folder.protected === true && folder.name === 'User Uploads'
        );
        
        if (!userUploadsFolder) {
            return { hasUserUploadsFolder: false, totalFiles: 0, filesBySource: {} };
        }
        
        const userUploadsFolderId = userUploadsFolder[0];
        
        // Count files in User Uploads folder
        const userUploadFiles = Object.values(files).filter(file => file.folderId === userUploadsFolderId);
        const filesBySource = {};
        
        userUploadFiles.forEach(file => {
            const source = file.uploadSource || file.migratedFrom || 'unknown';
            filesBySource[source] = (filesBySource[source] || 0) + 1;
        });
        
        return {
            hasUserUploadsFolder: true,
            userUploadsFolderId,
            totalFiles: userUploadFiles.length,
            filesBySource
        };
        
    } catch (error) {
        console.error('Error getting user uploads stats:', error);
        return { hasUserUploadsFolder: false, totalFiles: 0, filesBySource: {} };
    }
};
